import { useEffect, useState } from "react";
import { CreditCard, CheckCircle2, AlertTriangle, Users, Loader2, Settings, MessageCircle, Boxes, Clock, UserPlus } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { TooltipButton, WithTooltip } from "@/components/common/Tooltips";
import { listResource, api } from "@/lib/api";
import { mxn, fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Arquitectura Payment Provider: multi-pasarela por configuración. Hoy solo Stripe visible/activa.
const PROVIDERS = ["Stripe"];

export default function Facturacion() {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [checkoutBusy, setCheckoutBusy] = useState(null);
  const [portalBusy, setPortalBusy] = useState(false);
  // ago-2026 — Productos de crédito por plan: contador usados/incluidos y solicitud del
  // producto extra ($500 MXN/mes + IVA, cobrado por transferencia; el super_admin lo activa).
  const [usoProd, setUsoProd] = useState(null);
  // sep-2026 — Altas de clientes incluidas en el plan: contador del periodo vigente
  // (mismo patrón que el de productos). 1 alta = 1 consulta real de listas.
  const [usoAltas, setUsoAltas] = useState(null);
  const [solicitando, setSolicitando] = useState(false);
  const isSaas = user?.role === "super_admin";

  const cargarUsoProductos = () => {
    if (user?.role !== "admin_entidad") return;
    api.get("/facturacion/uso-productos")
      .then((r) => setUsoProd(r.data || null))
      .catch(() => setUsoProd(null));
  };

  const cargarUsoAltas = () => {
    if (user?.role !== "admin_entidad") return;
    api.get("/facturacion/uso-creditos")
      .then((r) => setUsoAltas(r.data?.creditos || null))
      .catch(() => setUsoAltas(null));
  };

  useEffect(() => {
    cargarUsoProductos();
    cargarUsoAltas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  const solicitarProductoExtra = async () => {
    setSolicitando(true);
    try {
      const { data } = await api.post("/facturacion/solicitar-producto-extra", { cantidad: 1 });
      toast.success("Solicitud registrada. La activamos al confirmar tu transferencia.");
      cargarUsoProductos();
      if (data?.whatsapp_url) window.open(data.whatsapp_url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo registrar la solicitud");
    } finally {
      setSolicitando(false);
    }
  };

  const reloadSub = () => {
    listResource("suscripciones").then((d) => setSub(d[0] || null));
  };

  // ago-2026 · Fase 2 — Catálogo AISLADO POR RÉGIMEN. El backend decide qué planes puede
  // contratar esta entidad (`/facturacion/planes-disponibles`): una entidad de actividad
  // vulnerable no recibe los planes SOFOM ni al revés. Antes se pintaba `meta.planes`,
  // que es el catálogo público completo de la landing.
  useEffect(() => {
    api.get("/facturacion/planes-disponibles")
      .then((r) => setPlanes(r.data?.planes || []))
      .catch(() => setPlanes([]));
  }, []);

  useEffect(() => {
    if (!isSaas) reloadSub();
  }, [isSaas]);

  // Retorno desde Stripe Checkout: confirma el pago por POLLING (Fase 1, sin webhook todavía).
  useEffect(() => {
    if (isSaas) return;
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (!sid) return;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/facturacion/checkout/status/${sid}`);
        if (data.payment_status === "paid") {
          toast.success("¡Suscripción activada! Pago confirmado.");
          window.history.replaceState({}, "", "/facturacion");
          reloadSub();
          return;
        }
        if (data.status === "expired") {
          toast.error("La sesión de pago expiró. Intenta de nuevo.");
          window.history.replaceState({}, "", "/facturacion");
          return;
        }
      } catch (e) { /* reintenta */ }
      if (attempts < 6) setTimeout(poll, 2000);
      else { window.history.replaceState({}, "", "/facturacion"); }
    };
    toast.message("Confirmando tu pago…");
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSaas]);

  const suscribir = async (plan) => {
    setCheckoutBusy(plan.id);
    try {
      const { data } = await api.post("/facturacion/checkout", {
        plan_id: plan.id, origin_url: window.location.origin,
      });
      if (data.url) window.location.href = data.url;
      else { toast.error("No se recibió la URL de pago"); setCheckoutBusy(null); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo iniciar el pago");
      setCheckoutBusy(null);
    }
  };

  const abrirPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/facturacion/portal", { origin_url: window.location.origin });
      if (data.url) window.location.href = data.url;
      else { toast.error("No se recibió la URL del portal"); setPortalBusy(false); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo abrir el portal de facturación");
      setPortalBusy(false);
    }
  };

  const vencida = sub && sub.estatus !== "Activa";
  const waUrl = `https://wa.me/522224860318?text=${encodeURIComponent("Hola, quiero recargar créditos para dar de alta más clientes en mi plan FELVORA.")}`;

  return (
    <div>
      <PageHeader title="Facturación y Suscripción SaaS" subtitle="Plan, estatus de suscripción y pagos recurrentes (arquitectura multi-pasarela)" />
      <div className="space-y-6 p-6 md:p-8">
        {isSaas && (
          <div data-testid="facturacion-saas-note" className="rounded-lg border border-slate-200 bg-slate-50 p-5">
            <p className="font-heading text-sm font-semibold text-slate-700">Vista de plataforma (FELVORA)</p>
            <p className="mt-1 text-sm text-slate-600">
              Como administrador del SaaS no gestionas una suscripción individual. Consulta los pagos, planes y
              estatus de cobro de cada entidad en <span className="font-medium text-primary">Admin SaaS → Resumen por entidad</span>.
            </p>
          </div>
        )}
        {sub && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-primary p-6 text-white shadow-sm lg:col-span-1">
              <div className="flex items-center justify-between">
                <CreditCard className="h-6 w-6 text-emerald-300" />
                <StatusBadge value={sub.estatus} />
              </div>
              <p className="mt-4 font-heading text-xl font-semibold">{sub.plan}</p>
              <p className="mt-1 font-heading text-3xl font-bold tabular-nums">{mxn(sub.monto)}<span className="text-sm font-normal text-slate-300">/{sub.periodicidad?.toLowerCase()}</span>
                {Number(sub.monto) > 0 && (
                  <span className="ml-1.5 whitespace-nowrap align-middle text-xs font-normal text-slate-300"
                        data-testid="suscripcion-mas-iva">+ IVA</span>
                )}
              </p>
              <div className="mt-4 space-y-1 text-sm text-slate-200">
                <p>Proveedor: <span className="font-medium text-white">{sub.proveedor_pago}</span></p>
                <p>Método: <span className="font-medium text-white">{sub.metodo_pago}</span></p>
                <p>Próximo cobro: <span className="font-medium text-white">{fdate(sub.proximo_cobro)}</span></p>
              </div>
              {user?.role === "admin_entidad" && sub.stripe_customer_id && (
                <TooltipButton tooltip="Abrir el portal de facturación de Stripe (cambiar método de pago, ver recibos)"
                  data-testid="abrir-portal-btn" variant="outline" size="sm" disabled={portalBusy}
                  onClick={abrirPortal}
                  className="mt-5 w-full border-white/30 bg-white/10 text-white hover:bg-white/20">
                  {portalBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Settings className="mr-1.5 h-4 w-4" />}
                  Administrar mi suscripción
                </TooltipButton>
              )}
              {user?.role === "admin_entidad" && sub.stripe_customer_id && (
                <p className="mt-2 text-[11px] leading-snug text-slate-300">
                  Cambia tu tarjeta, consulta y descarga tus facturas en el portal seguro de Stripe.
                  Para cancelar tu suscripción, contáctanos.
                </p>
              )}
            </div>

            <div className="lg:col-span-2">
              {vencida ? (
                <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-5">
                  <AlertTriangle className="h-6 w-6 shrink-0 text-red-600" />
                  <div>
                    <p className="font-heading font-semibold text-red-700">Suscripción {sub.estatus?.toLowerCase()}</p>
                    <p className="mt-1 text-sm text-red-600">
                      Tu suscripción requiere regularización. Tras el periodo de gracia ({sub.periodo_gracia_dias} días) el acceso se limita,
                      sin borrar información. Solo el administrador podrá regularizar el pago; al confirmarse, el acceso se reactiva automáticamente.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-5">
                  <CheckCircle2 className="h-6 w-6 shrink-0 text-emerald-600" />
                  <div>
                    <p className="font-heading font-semibold text-emerald-700">Suscripción activa</p>
                    <p className="mt-1 text-sm text-emerald-600">Último pago exitoso: {fdate(sub.ultimo_pago_exitoso)}. Intentos de cobro: {sub.intentos_cobro || 0}.</p>
                  </div>
                </div>
              )}

              <div className="mt-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <p className="mb-3 font-heading text-sm font-semibold text-slate-700">Pasarelas de pago soportadas (arquitectura Payment Provider)</p>
                <div className="flex flex-wrap gap-2">
                  {PROVIDERS.map((p) => (
                    <span key={p} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-600">{p}</span>
                  ))}
                </div>
                <p className="mt-3 text-xs text-slate-400">La plataforma opera con Stripe como proveedor de cobro. La arquitectura Payment Provider permite habilitar otras pasarelas por configuración en el futuro. Pagos recurrentes vía API preparados (Customer ID, Subscription ID, webhooks, reintentos).</p>
              </div>
            </div>
          </div>
        )}

        {usoAltas?.enforcement && (
          <div data-testid="altas-plan-card"
            className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <UserPlus className="h-5 w-5 text-primary" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm font-semibold text-slate-800">
                  Altas de clientes incluidas en tu plan
                </p>
                {usoAltas.unlimited ? (
                  <p className="mt-1 text-sm text-slate-600" data-testid="altas-plan-ilimitado">
                    Tu plan incluye <b>altas de clientes ilimitadas</b>.
                  </p>
                ) : (
                  <>
                    <p className="mt-1 text-sm text-slate-600">
                      Usas{" "}
                      <b data-testid="altas-plan-contador">
                        {Number(usoAltas.usados || 0).toLocaleString("es-MX")} de{" "}
                        {Number(usoAltas.limite_mes || 0).toLocaleString("es-MX")}
                      </b>{" "}
                      altas de clientes con screening de listas este mes
                      <span className="text-slate-400"> · periodo {usoAltas.periodo}</span>.
                    </p>
                    <div className="mt-3 h-2 w-full max-w-md overflow-hidden rounded-full bg-slate-100">
                      <div className={
                        "h-full rounded-full " +
                        (usoAltas.disponibles_incluidos === 0 ? "bg-red-500"
                          : usoAltas.usados >= usoAltas.limite_mes * 0.8 ? "bg-amber-500" : "bg-emerald-500")}
                        style={{ width: `${Math.min(100, (usoAltas.usados / Math.max(1, usoAltas.limite_mes)) * 100)}%` }} />
                    </div>
                    <p className="mt-2 text-xs text-slate-500" data-testid="altas-plan-disponibles">
                      Te quedan <b>{Number(usoAltas.disponibles_incluidos || 0).toLocaleString("es-MX")}</b> altas incluidas
                      {usoAltas.wallet > 0
                        ? <> + <b data-testid="altas-plan-wallet">{Number(usoAltas.wallet).toLocaleString("es-MX")}</b> de recarga disponibles</>
                        : null}. El contador se reinicia el primer día de cada mes.
                    </p>
                    {usoAltas.precio_alta_adicional > 0 && (
                      <p className="mt-3 text-sm text-slate-600" data-testid="altas-precio-adicional">
                        Alta de cliente adicional: <b>{mxn(usoAltas.precio_alta_adicional)}</b>
                        <span className="ml-1 text-xs text-slate-400">+ IVA</span>
                        <span className="ml-1 text-xs text-slate-500">
                          · se recarga a tu cuenta al confirmar el pago (lo activamos manualmente)
                        </span>
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {usoProd?.enforcement && (
          <div data-testid="productos-plan-card"
            className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                <Boxes className="h-5 w-5 text-primary" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-heading text-sm font-semibold text-slate-800">
                  Productos de crédito de tu plan
                </p>
                {usoProd.unlimited ? (
                  <p className="mt-1 text-sm text-slate-600" data-testid="productos-plan-ilimitado">
                    Tu plan incluye <b>productos de crédito ilimitados</b>. Tienes{" "}
                    <b>{Number(usoProd.activos || 0).toLocaleString("es-MX")}</b> activos.
                  </p>
                ) : (
                  <>
                    <p className="mt-1 text-sm text-slate-600">
                      Usas{" "}
                      <b data-testid="productos-plan-contador">
                        {usoProd.activos} de {usoProd.limite_efectivo}
                      </b>{" "}
                      productos ({usoProd.limite_plan} incluidos en el plan
                      {usoProd.productos_extra > 0 ? ` + ${usoProd.productos_extra} extra` : ""}).
                    </p>
                    <div className="mt-3 h-2 w-full max-w-md overflow-hidden rounded-full bg-slate-100">
                      <div className={
                        "h-full rounded-full " +
                        (usoProd.disponibles === 0 ? "bg-red-500"
                          : usoProd.disponibles <= 1 ? "bg-amber-500" : "bg-emerald-500")}
                        style={{ width: `${Math.min(100, (usoProd.activos / Math.max(1, usoProd.limite_efectivo)) * 100)}%` }} />
                    </div>
                    {usoProd.precio_extra > 0 && (
                      <p className="mt-3 text-sm text-slate-600" data-testid="productos-precio-extra">
                        Producto de crédito adicional: <b>{mxn(usoProd.precio_extra)}/mes</b>
                        <span className="ml-1 text-xs text-slate-400">+ IVA</span>
                        <span className="ml-1 text-xs text-slate-500">
                          · pago por transferencia bancaria (lo activamos manualmente)
                        </span>
                      </p>
                    )}
                    <div className="mt-4">
                      {usoProd.solicitud_pendiente ? (
                        <span data-testid="productos-solicitud-pendiente"
                          className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800">
                          <Clock className="h-3.5 w-3.5" />
                          Solicitud de {usoProd.solicitud_pendiente.cantidad} producto(s) en revisión
                        </span>
                      ) : (
                        <TooltipButton tooltip="Registra la solicitud del producto adicional y abre WhatsApp con el mensaje listo"
                          data-testid="solicitar-producto-extra-btn" size="sm" disabled={solicitando}
                          onClick={solicitarProductoExtra}
                          className="bg-accent hover:bg-forest-700">
                          {solicitando ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                            : <Boxes className="mr-1.5 h-4 w-4" />}
                          Solicitar producto de crédito extra
                        </TooltipButton>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        <div>
          <h3 className="mb-3 font-heading text-base font-semibold text-slate-800">Planes disponibles</h3>
          {!isSaas && user?.role === "admin_entidad" && (
            <WithTooltip text="Contactar por WhatsApp para recargar altas de clientes">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wa-producto-adicional"
              className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 transition-colors hover:bg-emerald-100"
            >
              <span className="flex items-center gap-2 text-sm font-medium text-emerald-800">
                <MessageCircle className="h-4 w-4 shrink-0 text-emerald-600" />
                ¿Necesitas recargar altas de clientes? Contáctanos por WhatsApp
              </span>
              <span className="text-xs font-semibold text-emerald-700">Escribir por WhatsApp →</span>
            </a>
            </WithTooltip>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {planes.map((p) => (
              <div key={p.id} data-testid={`plan-card-${p.id}`} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <p className="font-heading text-base font-semibold text-slate-900">{p.nombre}</p>
                <p className="mt-1 font-heading text-2xl font-bold text-primary tabular-nums">
                  {p.es_cotizacion ? "A medida" : mxn(p.precio_mensual)}{!p.es_cotizacion && <span className="text-xs font-normal text-slate-400">/mes</span>}
                  {!p.es_cotizacion && (
                    <span className="ml-1.5 whitespace-nowrap align-middle text-xs font-normal text-slate-400"
                          data-testid={`precio-mas-iva-${p.id}`}>+ IVA</span>
                  )}
                </p>
                <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
                  <li className="font-medium text-slate-700">Clientes ilimitados</li>
                  {Number(p.tipos_producto_incluidos) >= 0 && (
                    <li data-testid={`plan-productos-${p.id}`}>
                      {p.tipos_producto_incluidos} producto{p.tipos_producto_incluidos === 1 ? "" : "s"} de crédito
                      {p.precio_tipo_adicional ? ` (+${mxn(p.precio_tipo_adicional)}/mes + IVA por producto extra)` : ""}
                    </li>
                  )}
                  {p.creditos_incluidos === -1
                    ? <li>Altas de clientes ilimitadas</li>
                    : (p.creditos_incluidos != null
                        ? <li>{Number(p.creditos_incluidos).toLocaleString("es-MX")} altas de clientes incluidas/mes{!p.es_cotizacion && p.precio_credito_adicional ? ` (+${mxn(p.precio_credito_adicional)}/alta adicional)` : ""}</li>
                        : null)}
                  <li>Usuarios ilimitados</li>
                  {(p.modulos || []).map((m) => <li key={m} className="flex items-center gap-1.5"><CheckCircle2 className="h-3 w-3 text-emerald-500" /> {m}</li>)}
                </ul>
                {!isSaas && user?.role === "admin_entidad" && (
                  p.es_cotizacion ? (
                    <p className="mt-3 text-center text-xs text-slate-400">Plan a cotización — contáctanos</p>
                  ) : p.stripe_price_id ? (
                    <TooltipButton tooltip="Ir al portal seguro de Stripe para pagar la membresía"
                      data-testid={`suscribir-${p.id}`} size="sm" disabled={checkoutBusy === p.id}
                      onClick={() => suscribir(p)}
                      className="mt-3 w-full bg-primary hover:bg-sidebar-hover">
                      {checkoutBusy === p.id ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                      {sub?.plan_id === p.id && sub?.stripe_subscription_id ? "Plan actual · Renovar" : "Suscribirme"}
                    </TooltipButton>
                  ) : (
                    <p className="mt-3 text-center text-[11px] text-slate-400">Pago en línea no disponible aún</p>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
